import { CSSProperties, useEffect, useRef, useState } from 'react';
import { previewPhrases } from './previewPhrases';
import { previewPhraseMinimumWidth, previewPhraseMusicXml } from './previewPhraseMusicXml';
import {
  RenderedPreviewPhrase,
  previewStaffSpace,
  renderPreviewPhraseSvg,
} from './renderPreviewPhraseSvg';
import { useElementSize } from './useElementSize';

// Widths are rounded down to this step, and each step is engraved once and remembered.
const WIDTH_STEP = 8;
// The box's width is followed as a resize happens (at most once a frame), not after it.
const FOLLOW_RESIZE_AS_IT_HAPPENS = 0;
// How far the last engraving may be squeezed or stretched across to a new width while that
// width is engraved. Past this the symbols would visibly distort (a window snapped to half,
// or the preview shown again much wider), so the last engraving is shown at its own width.
const LEAST_STRETCH = 0.85;
const MOST_STRETCH = 1.2;
const MAX_REMEMBERED_RENDERS = 64;

// Thrown for an engraving that was dropped before it started because its level or size
// was no longer the one on screen; not a failure to draw.
class StaleEngraving extends Error {
  constructor() {
    super('The preview phrase was no longer on screen');
    // Keeps `instanceof` working where classes are compiled down to functions.
    Object.setPrototypeOf(this, StaleEngraving.prototype);
  }
}

// Runs `work` once the browser is idle (or soon after an input), never in the middle of
// the tap or resize that asked for it.
const whenIdle = (work: () => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(work, { timeout: 100 });
  } else {
    setTimeout(work, 0);
  }
};

const renders = new Map<string, Promise<RenderedPreviewPhrase>>();
// For an engraving still waiting for its idle moment: whether anyone who asked for it
// still has it on screen. A level left and returned to before the job runs adds its check
// to that job's list, so the job runs for it instead of being dropped as stale. Each job
// keeps its own list: a newer job under the same key (after the older one fell out of the
// cache) never takes over or empties it.
const waiting = new Map<string, Array<() => boolean>>();

const engrave = (
  levelNumber: number,
  width: number,
  staffSpace: number,
  stillWanted: () => boolean,
) => {
  const key = `${levelNumber}|${width}|${staffSpace.toFixed(3)}`;
  const remembered = renders.get(key);
  if (remembered) {
    waiting.get(key)?.push(stillWanted);
    return remembered;
  }
  const checks = [stillWanted];
  waiting.set(key, checks);
  const phrase = previewPhrases[levelNumber];
  const render = new Promise<RenderedPreviewPhrase>((resolve, reject) => {
    whenIdle(() => {
      if (waiting.get(key) === checks) waiting.delete(key);
      // Flicking through levels: only a level still on screen is engraved.
      if (!checks.some((isWanted) => isWanted())) {
        reject(new StaleEngraving());
        return;
      }
      renderPreviewPhraseSvg(
        previewPhraseMusicXml(phrase),
        phrase.treble.length,
        width,
        staffSpace,
      ).then(resolve, reject);
    });
  });
  // Forget a failed or dropped job, but never a newer one stored under the same key.
  render.catch(() => renders.get(key) === render && renders.delete(key));
  renders.set(key, render);
  if (renders.size > MAX_REMEMBERED_RENDERS) {
    renders.delete(renders.keys().next().value as string);
  }
  return render;
};

interface PreviewPhraseProps {
  levelNumber: number;
  // "Level 9: 2-Part Harmony", read out in place of the notation.
  levelTitle: string;
  // The level's preview picture, shown when there is no notation or it fails to draw.
  fallbackSrc: string;
  // The phrase's height in CSS pixels; it stays this height at every width.
  height: number;
}

interface Engraving {
  levelNumber: number;
  width: number;
  staffSpace: number;
  result: RenderedPreviewPhrase;
}

// The level's preview phrase as live notation: it fills the width it is given at a fixed
// height, the notes spreading out or closing up while every symbol keeps its size.
export const PreviewPhrase = ({
  levelNumber,
  levelTitle,
  fallbackSrc,
  height,
}: PreviewPhraseProps) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const boxWidth = useElementSize(boxRef, FOLLOW_RESIZE_AS_IT_HAPPENS).width;
  const staffSpace = previewStaffSpace(height);
  const phrase = previewPhrases[levelNumber];
  const hasNotation = !!phrase;
  // The surfaces hide the preview below the width where it reads; this guards the phrase
  // itself. The box's own width decides, so a box the surface shows always gets notation;
  // the engraving width is then stepped down to the 8px grid, but never below the minimum.
  const minimumWidth = hasNotation ? previewPhraseMinimumWidth(phrase, staffSpace) : 0;
  const fits = height > 0 && hasNotation && boxWidth > 0 && boxWidth >= minimumWidth;
  const width = fits ? Math.max(Math.floor(boxWidth / WIDTH_STEP) * WIDTH_STEP, minimumWidth) : 0;
  const [engraving, setEngraving] = useState<Engraving | null>(null);
  const [failed, setFailed] = useState<{ levelNumber: number; width: number } | null>(null);
  const failedHere = failed?.levelNumber === levelNumber && failed.width === width;
  const upToDate =
    engraving?.levelNumber === levelNumber &&
    engraving.width === width &&
    engraving.staffSpace === staffSpace;
  // One engraving at a time. While it is drawn the box may go on resizing (a window drag);
  // when it lands, the newest width is drawn next and the widths passed over are skipped,
  // so the phrase keeps up with the drag without a queue of engravings behind it.
  const [drawing, setDrawing] = useState(false);
  const levelOnScreen = useRef(levelNumber);
  levelOnScreen.current = levelNumber;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasNotation || !fits || drawing || upToDate || failedHere) return;
    setDrawing(true);
    // An engraving for an earlier width of the same level is still worth showing (the
    // newest width follows it); one for a level no longer on screen is not.
    const stillOnScreen = () => mounted.current && levelOnScreen.current === levelNumber;
    engrave(levelNumber, width, staffSpace, stillOnScreen)
      .then(
        (result) => stillOnScreen() && setEngraving({ levelNumber, width, staffSpace, result }),
        (error) =>
          stillOnScreen() &&
          !(error instanceof StaleEngraving) &&
          setFailed({ levelNumber, width }),
      )
      .finally(() => mounted.current && setDrawing(false));
  }, [levelNumber, width, staffSpace, hasNotation, fits, drawing, upToDate, failedHere]);

  // Without ResizeObserver the width cannot be followed, so the picture stands in.
  const canFollowWidth = typeof ResizeObserver !== 'undefined';
  const showsPicture = !hasNotation || failedHere || !canFollowWidth;
  // While a new width is being engraved the last one stays up, stretched or squeezed
  // across to the new width for those few frames, so it fills the box as the box moves;
  // after a jump too big for that, it stays at its own width until the new one lands.
  const shown = !showsPicture && engraving?.levelNumber === levelNumber ? engraving : null;
  const widthRatio = shown && width > 0 ? width / shown.width : 1;
  const stretch = widthRatio >= LEAST_STRETCH && widthRatio <= MOST_STRETCH ? widthRatio : 1;

  return (
    <div
      ref={boxRef}
      role="img"
      aria-label={`Preview of ${levelTitle}`}
      data-testid="preview-phrase"
      data-preview={showsPicture ? 'picture' : 'notation'}
      style={{ ...boxStyle, height: `${height}px` }}
    >
      {showsPicture && fallbackSrc && (
        <img src={fallbackSrc} alt="" aria-hidden="true" style={pictureStyle} />
      )}
      {shown && fits && (
        <div
          aria-hidden="true"
          data-width={shown.width}
          style={{
            position: 'absolute',
            left: 0,
            top: `${shown.result.offsetTop}px`,
            transform: stretch === 1 ? undefined : `scaleX(${stretch})`,
            transformOrigin: 'left top',
          }}
          dangerouslySetInnerHTML={{ __html: shown.result.svg }}
        />
      )}
    </div>
  );
};

const boxStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

const pictureStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};
